import {
  ICalendarUrl, IEmail, IMailAuth, INewCalendar, INotification, IProfileInfo, IProfileInfoDto,
  ISynchronization, MAX_NOTIFICATIONS_COUNT, ProfileRequest,
  TCalendarProvider, getCryptedString, timeLabels
} from '@utils';
import { LoginViewModel } from '@views';
import { AxiosResponse } from 'axios';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { Inject, Service } from 'typedi';
import { PrivateCalendarsStore, TimeZonesStore, CalendarColorsStore } from './../../store';
import { getAvatarSrc, AuthService } from './../../utils';

@Service()
export class ProfileViewModel {
  /** Объект класса с реквестами почты, календарей, информации профиля и т.д. */
  @Inject(() => ProfileRequest)
  private profileRequest: ProfileRequest;

  /** Стор с подключенными календарями. */
  @Inject(() => PrivateCalendarsStore)
  private privateCalendarsStore: PrivateCalendarsStore;

  /** Стор с цветами доступными на бекенде. */
  @Inject(() => CalendarColorsStore)
  private calendarColorsStore: CalendarColorsStore;

  /** Стор с часовыми поясами. */
  @Inject(() => TimeZonesStore)
  private timeZonesStore: TimeZonesStore;

  @Inject(() => LoginViewModel)
  private loginVM: LoginViewModel;

  /** Главная информация о профиле. */
  @observable.ref profileInfo: IProfileInfo = {
    img: "",
    fullName: "",
    corporateEmail: "",
    position: "",
  };

  initPromise: Promise<void | []> = null;

  // ************************************* ЧАСОВЫЕ ПОЯСА ****************************************

  /** Часовой пояс пользователя. */
  @observable timeZoneId: number | undefined = undefined;

  /** Автоопределение часового пояса. */
  @observable useLocalTimeZone = true;

  // ********************************** ЭЛЕКТРОННЫЕ ПОЧТЫ ***************************************

  /** Переменная в которой хранится подключаемая электронная почта. */
  @observable newEmail = '';

  /** Сторонние электронные почты. */
  @observable.shallow mutableEmails: IEmail[] = [];

  /** Переменная отслеживающая открыт ли input с вводом новой почты. */
  @observable isCreateNewEmail = false;

  @observable isLoading = false;

  // ************************************** КАЛЕНДАРИ *******************************************

  /** Подключенные пользователем календари. */
  @observable.shallow urls: ICalendarUrl[] = [];

  /** Подключаемый пользователем календарь. */
  @observable.ref newCalendar: ICalendarUrl | undefined = undefined;

  /** Переменная для восстановления состояния календаря при возвращении со страницы туториала */
  @observable restoreNewCalendar = false;

  /** 
   * Провайдер для подключаемого календаря.
   * @example MAIL - mail.ru  
   */
  @observable calendarProvider: TCalendarProvider = 'THUNDERBIRD';

  /**
   * Авторизационные данные для подключения календаря mail.ru.
   */
  @observable mailAuth: IMailAuth | undefined = undefined;

  /** Открыто ли окно подключения нового календаря. */
  @observable isConnectNewCalendar = false;

  /** Редактируемый календарь. */
  @observable.ref editCalendar: ICalendarUrl | undefined = undefined;

  /** Статус синхронизации подключенных календарей. */
  @observable.ref synchronizationStatus: ISynchronization | undefined = undefined;

  // ************************************* УВЕДОМЛЕНИЯ ******************************************

  /** 
   * Время отключения уведомлений.
   * @example 0 - Уведомления включены.
   * -1 - Уведомления выключены.
   * N - Уведомления отключены на N минут.
  */
  @observable reminderOffMinutes: number = 0;

  /** 
   * Настроенные уведомления.
   */
  @observable.shallow notifications: INotification[] = [];

  /**
   * Новая настройка уведомлений.
   */
  @observable newNotification: INotification | undefined = undefined;

  @observable calendarLoading = false;

  /**
   * Получение списка доступных опций для настройки уведомлений.
   */
  @computed get freeTimes() {
    return timeLabels.filter(({ value }) => !this.notifications.some(n => n.minutes === value));
  }

  constructor() {
    makeObservable(this);
  }

  // *************************************** ACTIONS ********************************************

  /** 
   * Сброс настроек.
   */
  @action updateSettings = () => {
    if (!this.restoreNewCalendar) this.isConnectNewCalendar = false;
    this.restoreNewCalendar = false;
    if (!this.isLoading) this.setTimeZone();
  };

  /**
   * Инициализация view модели.
   */
  @action onInit = () => {
    if (this.loginVM.authorized && !this.isLoading) {
      this.setLoading(true);
      this.initPromise = Promise.all([
        this.profileRequest.getSyncStatus(),
        this.timeZonesStore.getTimeZones(),
        this.getNotifications(),
        this.calendarColorsStore.getColors(),
        this.getCalendars(),
        this.profileRequest.getUserInformation()
          .then((resp) => this.getUserInfoFromResponse(resp)),
        this.initEmails(),
      ])
        .then(() => {
          this.setLoading(false);
          this.setTimeZone();
        });
    }
    return this.initPromise;
  };

  /**
   * Инициализация электронных почт.
   */
  @action initEmails = () => {
    this.getEmails();
    if (this.isCreateNewEmail)
      this.switchNewEmailFieldVisibility();
  };

  /**
   * Функция установки или удаления лоадера на странице профиля.
   * @param value true - Показать лоадер. false - Скрыть.
   */
  @action setLoading = (value: boolean) => {
    this.isLoading = value;
  };

  // **************************** UTC ****************************

  /**
   * Установка часового пояса. 
   * Если вызывается без параметров, то он обновит ЧП, только в том случае, если useLocalTimeZone === true. 
   * @param useLocalTimeZone : Используется ли локальный ЧП пользователя.
   * @param timeZoneId : ID ЧП пользователя, на которыйй мы меняем.
   */
  @action setTimeZone = (useLocalTimeZone: boolean = this.useLocalTimeZone, timeZoneId: number = this.timeZoneId) => {
    if (useLocalTimeZone) {
      const offsetString = this.getUtcString(-(new Date().getTimezoneOffset()) / 60);

      timeZoneId = this.timeZonesStore
        .getTimeZoneIdFromOffset(offsetString)
        ?.id;
    }
    if (
      useLocalTimeZone !== undefined && timeZoneId &&
      (useLocalTimeZone !== this.useLocalTimeZone || timeZoneId !== this.timeZoneId)
    ) {
      this.profileRequest.setTimeZone({
        timeZoneId,
        useLocalTimeZone,
      }).then(() => runInAction(() => {
        this.timeZoneId = timeZoneId;
        this.useLocalTimeZone = useLocalTimeZone;
      }));
    }
  };

  /**
   * Получение по id строкового представления часового пояса.
   * @param id id часового пояса.
   * @returns Строковое представление часового пояса.
   */
  getTimeZoneLabel = (id: number) => this.timeZonesStore.getStringifyTimeZone(id);

  /**
   * Переключение с использования времени системы на кастомный ЧП и обратно.
   */
  switchUseLocalTimeZone = () => this.setTimeZone(!this.useLocalTimeZone);

  /**
   * Получение смещения часового пояса в минутах.
   */
  @computed get minutesOffset () { 
    return this.timeZonesStore.getTimeZone(this.timeZoneId)?.minutesOffset ?? 0;
  }

  // ****************** NOTIFICATION ********************

  /** 
   * Получение всех уведомлений. 
   */
  @action getNotifications = () =>
    this.profileRequest.getNotifications()
      .then((resp) => runInAction(() => this.notifications = resp.data));

  /**
   * Удаление уведомления в соответствии с минутами.
   * @param minutes Минуты уведомления, которое надо удалить.
   */
  @action deleteNotification = (minutes: number) =>
    this.profileRequest.deleteNotification(minutes)
      .then(() =>
        this.notifications = this.notifications.filter(notification => notification.minutes !== minutes));

  /**
   * Редактирование уведомления, на вход получаем объект уведомления, который замещает
   * исходное, в соответствии с его временем.
   * @param updatedNotification Новое уведомление.
   */
  @action editNotification = (updatedNotification: INotification) =>
    this.profileRequest.editNotification(updatedNotification)
      .then(() =>
        this.notifications = this.notifications.map(notification =>
          notification.minutes === updatedNotification.minutes
            ? updatedNotification
            : notification
        ));

  /**
   * Изменение нового уведомления.
   * @param notification Уведомление, которое станет newNotification.
   */
  @action editNewNotification = (notification: INotification | null) =>
    this.newNotification = notification;

  /**
   * Добавление уведомления, которое хранится в newNotification.
   */
  @action addNotification = () => {
    if (this.notifications.length < MAX_NOTIFICATIONS_COUNT)
      this.profileRequest.addNotification(this.newNotification)
        .then(() => runInAction(() => {
          this.notifications.push(this.newNotification);
          this.newNotification = null;
        }));
    else
      toast.error(`Лимит до ${MAX_NOTIFICATIONS_COUNT}-х уведомлений`);
  };

  /** 
   * Включение/отключение оповещений.
   */
  @action changeAlertsState = (value: number) => {
    this.profileRequest.updateReminderSettings(value)
      .then(() => runInAction(() => this.reminderOffMinutes = value));
  };
  /**
   * Получение состояния уведомлений (включены/отключены).
   */
  @action getAlertState = () => {
    this.profileRequest.getReminderSetting()
      .then((resp) => runInAction(() => this.reminderOffMinutes = resp.data));
  }

  /** 
   * Смена видимости нового уведомления.
  */
  @action changeNewNotificationVisibility = () =>
    this.newNotification = this.newNotification
      ? null
      : {
        minutes: this.freeTimes[0].value,
        types: {
          isEmailSendingEnabled: true,
          isMattermostSendingEnabled: true
        }
      };


  // ********************** CALENDAR ************************
  @action initEditCalendar = (id: number | null) => {
    this.editCalendar = id === null ? null : this.urls.find((elem) => elem.id === id);
  };

  /**
   * Изменение авторизационных данных для календаря mail.ru.
   * @param value Значение которое станет авторизационными данными.
   */
  @action changeMailAuth = (value: IMailAuth) => {
    this.mailAuth = value;
  };

  /**
   * Получение статуса синхронизации календарей.
   * @returns Статус синхронизации и последнее его время. 
   */
  @action getSynchronizationStatus = () => {
    return this.profileRequest.getSyncStatus().then((resp) => runInAction(() => {
      this.synchronizationStatus = {
        status: resp.data.status,
        lastSyncTime: new Date(resp.data.lastSyncTime)
      };
    }));
  };

  /**
   * Синхронизация календарей.
   */
  synchronize = () => {
    this.profileRequest.synchronize();
  };

  /**
   * Изменение календаря.
   * @param calendar Значение которое станет новым значением календаря.
   */
  @action onChangeCalendar = (calendar: ICalendarUrl) => {
    this.editCalendar = calendar;
  };

  /**
   * Сохранение измененного календаря.
   */
  @action onChangeApply = () => {
    return this.profileRequest.editCalendar({
      calendarColor: this.editCalendar.calendarColor,
      calendarId: this.editCalendar.id,
      title: this.editCalendar.title,
      calendarType: this.editCalendar.calendarType,
    })
      .then(() => runInAction(() => {
        this.urls = this.urls.map((elem) => elem.id === this.editCalendar.id ? this.editCalendar : elem);
        this.editCalendar = null;
      }));
  };

  /**
   * Сброс состояния нового календаря.
  */
  @action resetNewCalendar = () => {
    this.newCalendar = {
      calendarColor: this.calendarColorsStore.data[0].colorName,
      calendarUri: '',
      title: '',
      calendarType: 'WORK',
    };
    this.calendarProvider = 'THUNDERBIRD';
  };

  @action initAuthCalendar = () => {
    this.mailAuth = {
      email: '',
      password: '',
    };
  };

  /** 
   * Обработчик изменения подключаемого календаря.
   * @param newCalendar: Новый календарь содержащий измененные uri, title и type.
   */
  @action editNewCalendar = (newCalendar: ICalendarUrl) => {
    this.newCalendar = newCalendar;
  };

  /** 
   * Функция подключения новой ссылки, которая находится в @observable поле newCalendar.
   */
  @action connectNewUrl = () => {
    const param: INewCalendar = { ...this.newCalendar, calendarProvider: this.calendarProvider };
    if (this.calendarProvider === 'MAIL') {
      param.email = this.mailAuth.email;
      param.password = this.mailAuth.password;
    }
    this.calendarLoading = true;
    return this.profileRequest.connectingNewCalendarUrl(param)
      .then(() => {
        runInAction(() => this.isConnectNewCalendar = false);
        this.resetNewCalendar();
        this.getCalendars().then(() => runInAction(() => this.calendarLoading = false));
      }).catch(() => this.calendarLoading = false);
  };

  /** 
   * Получение календарей.
   */
  @action getCalendars = () => {
    return this.privateCalendarsStore.getConnectingUrls()
      .then(() =>
        runInAction(() => this.urls = this.privateCalendarsStore.data)
      );
  };

  /**
   * Удаление календаря по id.
   * @param id ID удаляемого календаря.
   */
  @action deleteCalendar = (id: number) => {
    this.profileRequest.deleteCalendarUrl(id)
      .then(() => 
        runInAction(() => this.urls = this.urls.filter((elem) => elem.id !== id))
      );
  };

  /** 
   * Закрытие окна добавления нового календаря.
  */
  @action hideConnectingCalendar = () => {
    this.newCalendar = {
      calendarColor: this.calendarColorsStore.data[0].colorName,
      calendarUri: '',
      title: '',
      calendarType: 'WORK',
    };
    this.calendarProvider = 'THUNDERBIRD';
    this.isConnectNewCalendar = false;
  };

  /**
   * Смена источника подключения календаря.
   * @param provider Источник подключения календаря.
   */
  @action changeCalendarProvider = (provider: TCalendarProvider) => {
    this.calendarProvider = provider;
  };

  /** 
   * Изменение видимости окна добавления нового календаря.
  */
  @action switchNewCalendarVisibility = () => {
    !this.isConnectNewCalendar && this.resetNewCalendar();
    this.isConnectNewCalendar = !this.isConnectNewCalendar;
  };

  /**
   * Установка переменной, восстанавливающей значение нового календаря
   * @param backupCalendar Переме
   */
  @action setRestoreNewCalendar = (restoreNewCalendar: boolean) => {
    this.restoreNewCalendar = restoreNewCalendar;
  };

  // ********************** USER INFO **********************

  /**
   * Получение информации о пользователе из response.
   * @param resp Response от бэка, где нам передается основная информация профиля.
   */
  @action getUserInfoFromResponse = (resp: AxiosResponse<IProfileInfoDto, unknown>) => {
    this.profileInfo = {
      ...this.profileInfo,
      img: getAvatarSrc(this.userId),
      fullName: resp.data.fullName,
      corporateEmail: resp.data.corporateEmail ?? "Hello",
      position: resp.data.position
    };

    this.timeZoneId = resp.data.timeZone.id;
    this.useLocalTimeZone = resp.data.useLocalTimeZone;
  };

  @computed get userId() {
    return AuthService.getUserId();
  }

  /** 
   * Получение электронной почты.
   */
  @action getEmails = () => {
    return this.profileRequest.getEmails()
      .then((resp) => {
        runInAction(() => {
          this.mutableEmails = resp.data.mutableEmails.map((elem) => elem);
        });
      });
  };

  /**
   * Функция удаления электронной почты.
   * @param email –– почта, которую надо удалить!
   */
  @action deleteEmail = (email: string) => {
    this.profileRequest.deleteEmail(email)
      .then(() => this.getEmails());
  };

  /** 
   * Редактирование вводимой электронной почты.
   * @param newEmail Новое значение вводимой почты.
   */
  @action editNewEmail(newEmail: string) {
    this.newEmail = newEmail;
  }

  /** 
   * Установка введенной электронной почты.
   */
  @action setNewEmail = () => {
    this.profileRequest.setNewEmail(this.newEmail)
      .then(() => {
        const key = `${getCryptedString(this.newEmail)}Timer`;
        sessionStorage.setItem(key, `${Date.now()}`)
        this.mutableEmails.push({ email: this.newEmail, isVerified: false });
      });

    this.isCreateNewEmail = false;
  };

  /**
   * Повторная отправка письма подтверждения.
   * @param value почта, которую требуется подтвердить.
   * @returns Промис запроса.
   */
  @action resendConfirmation = (value: string) => {
    return this.profileRequest.resendEmail(value)
      .then((resp) => {
        if(resp.data.length === 0) {
          toast.success(`Письмо подтверждения отправлено на почту`)
        } else {
          toast.error(resp.data);
        }
      });
  }

  /** 
   * Очищает поле ввода новой электронной почты, если открывается строка для ее ввода. 
  */
  @action switchNewEmailFieldVisibility = () => {
    !this.isCreateNewEmail && (this.newEmail = '');
    this.isCreateNewEmail = !this.isCreateNewEmail;
  };

  // ************************************** FUNCTIONS *******************************************

  /**
   * Получение цветов.
   */
  @computed get colors() {
    return this.calendarColorsStore.colorsWithoutInnerColor;
  }

  /**
   * Получение строкового представления статуса синхронизации.
   */
  @computed get syncStatusString() {
    const status = this.synchronizationStatus?.status;
    const time = this.synchronizationStatus?.lastSyncTime;
    const timeString = `${time?.toTimeString().split(' ')[0]}`;

    if (status === 'LAST_SYNC_FAILED') {
      return `Последняя синхронизация провалилась в ${timeString}`;
    }
    if (status === 'LAST_SYNC_SUCCEEDED') {
      return `Последнее обновление - ${timeString}`;
    }
    if (status === 'OVERALL_SYNC_IS_RUNNING') {
      return 'Проходит общая синхронизация';
    }
    return 'Запущена пользовательская синхронизация';
  }

  /** 
   * Получение цвета по его имени.
   */
  getColor = (name: string) => this.calendarColorsStore.getColorByName(name);

  /** 
   * Получение списка id, из стора ЧП.
   */
  get timeZones() {
    return this.timeZonesStore.timeZonesIds;
  }

  /** 
   * Получение строки часового пояса.
   * @param timeZone числовой аналог выбранного часового пояса.
   * @returns строку формата ±hh:mm, где hh – часы, mm – минуты.
   * @example timeZone: 8.5 -> return: 08:30;
   * timeZone: -3.75 -> return: -3:45;
   * timeZone: 12 -> return: 12:00;
   */
  private getUtcString(timeZone: number) {
    const sign = timeZone >= 0 ? '+' : '-';
    const utcArr = `${Math.abs(timeZone)}`.split('.');
    if (utcArr.length === 1) {
      return `${sign + (utcArr[0].length === 1 ? `0${utcArr[0]}` : `${utcArr[0]}`)}:00`;
    }
    if (utcArr[1] === '5') {
      return `${sign + (utcArr[0].length === 1 ? `0${utcArr[0]}` : `${utcArr[0]}`)}:30`;
    }
    return `${sign + (utcArr[0].length === 1 ? `0${utcArr[0]}` : `${utcArr[0]}`)}:45`;
  }
}
